/**
 * Frontend API client for Company-Test Assignment endpoints (11-iframe-embed/01).
 * Thin wrapper over `fetch`; no state, no business logic.
 *
 * Mirrors `state/share-token/api.ts` style. State integration (TanStack Query
 * hooks) layers in with the parent feature's app-shell issue.
 */
import { apiFetch } from '@/lib/api-client';

export interface AssignedTestItem {
  id: string;
  title: string;
  description: string;
  status: string;
  track: string | null;
  level: string | null;
  author_name_at_publish: string | null;
  time_limit: number;
  estimated_minutes: number;
  pass_mark: number;
  issue_certificate: boolean;
  assigned_at: string;
  created_at: string;
}

export interface AssignedTestsPage {
  items: AssignedTestItem[];
  next_cursor: string | null;
}

export interface EmbedAudit {
  last_seen_referer: string | null;
  embed_load_count: number;
}

type Envelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string } };

async function unwrap<T>(res: Response): Promise<T> {
  if (res.status === 403) {
    // Surface as a recognisable message the page layer can pattern-match to
    // distinguish "not a member" from generic load failure.
    let code = 'forbidden';
    try {
      const j = (await res.json()) as Envelope<T>;
      if (!j.ok) code = j.error.code;
    } catch {
      // ignore parse failure — status 403 is enough
    }
    throw new Error(code === 'forbidden' ? 'not an active member of this Company' : code);
  }
  let json: Envelope<T>;
  try {
    json = (await res.json()) as Envelope<T>;
  } catch {
    throw new Error(`server returned ${res.status} ${res.statusText}`);
  }
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function listAssignedTestsForCompany(
  baseUrl: string,
  companyId: string,
  cursor?: string,
  limit?: number,
): Promise<AssignedTestsPage> {
  const q = new URLSearchParams();
  if (cursor !== undefined) q.set('cursor', cursor);
  if (limit !== undefined) q.set('limit', String(limit));
  const qs = q.toString();
  const url = `${baseUrl}/api/companies/${companyId}/assigned-tests${qs ? `?${qs}` : ''}`;
  const res = await apiFetch(url, { credentials: 'include' });
  return unwrap<AssignedTestsPage>(res);
}

export async function assignTestToCompany(
  baseUrl: string,
  companyId: string,
  testId: string,
): Promise<{ assigned: boolean; assigned_at: string }> {
  const res = await apiFetch(
    `${baseUrl}/api/admin/companies/${companyId}/test-assignments`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ test_id: testId }),
      credentials: 'include',
    },
  );
  return unwrap<{ assigned: boolean; assigned_at: string }>(res);
}

export async function unassignTestFromCompany(
  baseUrl: string,
  companyId: string,
  testId: string,
): Promise<{ unassigned: boolean }> {
  const res = await apiFetch(
    `${baseUrl}/api/admin/companies/${companyId}/test-assignments/${testId}`,
    { method: 'DELETE', credentials: 'include' },
  );
  return unwrap<{ unassigned: boolean }>(res);
}

export async function fetchEmbedSnippet(
  baseUrl: string,
  shareTokenId: string,
): Promise<{ snippet: string }> {
  const res = await apiFetch(
    `${baseUrl}/api/share-tokens/${shareTokenId}/embed-snippet`,
    { method: 'POST', credentials: 'include' },
  );
  return unwrap<{ snippet: string }>(res);
}

export async function fetchEmbedAudit(
  baseUrl: string,
  shareTokenId: string,
): Promise<EmbedAudit> {
  const res = await apiFetch(
    `${baseUrl}/api/admin/share-tokens/${shareTokenId}/embed-audit`,
    { credentials: 'include' },
  );
  return unwrap<EmbedAudit>(res);
}
